import * as React from 'react';
import {createViewConfig, view, dynamic, media} from '@sail/engine';
import type {View} from '@sail/engine';
import {tokens, css} from '@sail/ui';
import {styles} from 'mkt-ssr/components/sail/TestimonialCard/TestimonialCardStyles';

import {CtaButtonConfig} from '@sail/helm';
import {
  CustomerLogoConfig,
  CustomerLogo,
  CustomerLogoProps,
} from 'mkt-ssr/components/sail/CustomerLogo/CustomerLogo';

export type TestimonialCardProps = {
  /**
   * The optional name of the author of the quote.
   */
  authorName?: string;
  /**
   * The optional role/title of the author of the quote.
   */
  authorRole?: string;
  /**
   * An SVG of the logo to be displayed.
   */
  logo: CustomerLogoProps['logo'];
  /**
   * The color (in HEX) of use as the background color of the TestimonialCard.
   */
  backgroundColor: string;
  /**
   * An optional call to action to be displayed at the bottom of the TestimonialCard.
   */
  cta?: React.ReactNode;
  /**
   * The quote to be displayed in the TestimonialCard.
   */
  quote: string;
  /**
   * The URL of an image to be used as the background of the TestimonialCard.
   */
  backgroundImage?: string;
  /**
   * The orientation of the content in the TestimonialCard.
   */
  orientation: 'horizontal' | 'vertical';

  subviews?: View.Subviews<{
    copyContainer: 'div';
    logoContainer: 'div';
    quoteContainer: 'blockquote';
  }>;
};

export const TestimonialCardConfig = createViewConfig({
  props: {} as TestimonialCardProps,
  name: 'TestimonialCard',
  defaults: {
    orientation: 'horizontal',
    backgroundColor: '#0A2540',
  },
});

/**
 * A component for displaying the testimonial from a Stripe user/customer.
 */
export const TestimonialCard = TestimonialCardConfig.createView(
  (props) => {
    const {
      authorName,
      authorRole,
      backgroundColor,
      backgroundImage,
      cta,
      logo,
      quote,
      subviews,
      ...restProps
    } = props;

    return (
      <view.div uses={[styles.container]} {...restProps}>
        <view.div inherits={props.subviews.copyContainer}>
          <view.blockquote
            uses={[styles.quoteContainer]}
            inherits={props.subviews.quoteContainer}
          >
            <view.p uses={[styles.quoteBody]}>{props.quote}</view.p>

            {props.authorName ? (
              <view.footer uses={[styles.quoteFooter]}>
                <view.span uses={[styles.quoteAuthor]}>
                  {props.authorName}
                </view.span>
                ,{` `}
                {props.authorRole && props.authorRole}
              </view.footer>
            ) : null}
          </view.blockquote>

          {props.cta ? props.cta : null}
        </view.div>

        {props.logo ? (
          <view.div
            inherits={props.subviews.logoContainer}
            className="logoContainer"
          >
            <CustomerLogo
              uses={[styles.logoSvg, styles.logoSvgUnprocessed]}
              colorWay="flat"
              logo={props.logo}
            />
          </view.div>
        ) : null}
      </view.div>
    );
  },
  {
    subviews: {
      copyContainer: {
        css: {
          stack: 'y',
          gap: 'large',
          height: 'fill',
          padding: 'xxlarge',
          paddingRight: '0',
        },
      },
      logoContainer: {
        css: {
          stack: 'x',
          alignX: 'center',
          position: 'relative',
          zIndex: '1',
        },
      },
    },
    variants: {
      orientation: {
        vertical: {
          css: {
            stack: 'y',
            alignX: 'stretch',
            padding: 0,
            backgroundSize: 'cover',
          },
          uses: [css({flexDirection: 'column-reverse'})],
          subviews: {
            copyContainer: {
              css: {
                width: 'fill',
                paddingTop: '0',
              },
            },
            logoContainer: {
              css: {
                width: 'fill',
                height: '180px',
              },
            },
          },
        },
        horizontal: {
          css: {
            stack: 'y',
            alignX: 'stretch',
            backgroundSize: 'cover',
            [media.up(tokens.viewport.medium)]: {
              stack: 'x',
              alignY: 'center',
            },
          },
          uses: [
            css({
              flexDirection: 'column-reverse',
              [media.up(tokens.viewport.medium)]: {
                flexDirection: 'row',
              },
            }),
          ],
          subviews: {
            copyContainer: {
              css: {
                width: 'fill',
                paddingTop: '0',
                paddingX: 'large',
                [media.up(tokens.viewport.medium)]: {
                  width: '1/2',
                  paddingRight: '0',
                  paddingTop: 'xxlarge',
                  paddingBottom: 'xxlarge',
                  paddingLeft: 'xxlarge',
                },
              },
            },
            logoContainer: {
              css: {
                width: 'fill',
                height: '180px',
                alignY: 'center',
                [media.up(tokens.viewport.medium)]: {
                  width: '1/2',
                  height: 'initial',
                  alignY: 'stretch',
                },
              },
            },
          },
        },
      },
    },

    uses: [
      dynamic((props) => {
        // const {orientation, backgroundColor, backgroundImage} = props;
        const intents = [];

        // TODO: re-enable when backgroundImage image hydration warning is resolved
        // we don't use this in the carousel for now too
        // Set the background gradient for both orientations
        // const backgroundGradient = unprocessedCss({
        //   background: `linear-gradient(${
        //     orientation === 'horizontal' ? '270deg' : '180deg'
        //   }, transparent calc( 50% *-1) , ${backgroundColor} 50% ),100% 50%/auto 100% no-repeat ${backgroundColor} url('${backgroundImage}')`,
        // });

        const ctaButtonCustomizations = CtaButtonConfig.customize({
          variants: {
            type: {
              link: {
                css: {
                  textColor: 'onAction.primary',
                  ':hover': {
                    textColor: 'onAction.primary',
                  },
                },
              },
              primary: {
                css: {
                  textColor: props.backgroundColor,
                  backgroundColor: 'surface',
                  ':hover:not(:active):not([aria-disabled="true"])': {
                    textColor: props.backgroundColor,
                    backgroundColor: 'surface.hover',
                    opacity: '0.8',
                    keyline: 'none',
                  },
                  ':active:not([aria-disabled="true"])': {
                    textColor: props.backgroundColor,
                    backgroundColor: 'surface.hover',
                  },
                },
                uses: [
                  css({
                    ':focus-visible': {
                      outline: 'none',
                    },
                  }),
                ],
              },
            },
          },
        });

        // intents.push(backgroundGradient);
        intents.push(ctaButtonCustomizations);

        return intents;
      }),
      CustomerLogoConfig.customize({
        css: {
          maxWidth: 'none',
          [media.up(tokens.viewport.large)]: {
            maxWidth: '300px',
          },
        },
      }),
    ],
  },
);
